import {
  Component,
  ElementRef,
  ViewChild,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  booleanAttribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { uniqueId } from 'lodash';
import { NgxMaskDirective } from 'ngx-mask';

import { IconComponent } from '../icon/icon.component';
import { InputSecurityDirective } from '../../directives/input-security/input-security.directive';

@Component({
  selector: 'aprova-input',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NgxMaskDirective, IconComponent, InputSecurityDirective],
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputComponent implements OnInit, OnChanges {
  private readonly _cdr = inject(ChangeDetectorRef);

  @ViewChild('input', { static: false })
  public inputRef: ElementRef<HTMLInputElement> | undefined;

  @ViewChild('appendRef', { static: true })
  public appendRef: ElementRef<HTMLDivElement> | undefined;

  @ViewChild('errorMessageRef', { static: true })
  public errorMessageRef: ElementRef<HTMLDivElement> | undefined;

  @Input()
  public id: string = uniqueId('aprova-input-');

  @Input()
  public dataTestid?: string;

  @Input()
  public inputType: 'text' | 'password' | 'email' | 'number' = 'text';

  @Input()
  public inputMode:
    | 'none'
    | 'text'
    | 'decimal'
    | 'numeric'
    | 'tel'
    | 'search'
    | 'email'
    | 'url' = 'text';

  @Input()
  public label: string = '';

  @Input()
  public placeholder: string = '';

  @Input()
  public control = new FormControl<string>('');

  @Input()
  public labelClass: string = 'block';

  @Input()
  public inputClass: string = 'w-full py-2.5 px-3.5 text-gray-900 text-base font-normal leading-4 focus:outline-none';

  @Input()
  public width: string = 'auto';

  @Input()
  public height: string = '44px';

  @Input()
  public readonly: boolean = false;

  @Input()
  public squared: boolean = false;

  @Input()
  public filled: boolean = false;

  @Input()
  public shadow: boolean = false;

  @Input()
  public disabled: boolean = false;

  @Input()
  public clearButton: boolean = false;

  @Input()
  public maxLength: string | number | null = null;

  @Input()
  public mask?: string;

  @Input()
  public required: boolean = false;

  @Input()
  public requiredTag: boolean = false;

  @Input()
  public optionalTag: boolean = false;

  @Input()
  public error: boolean = false;

  @Input()
  public errorMessages: Record<string, string> = {};

  @Input({ transform: booleanAttribute })
  public secureInput: boolean = false;

  @Input({ transform: booleanAttribute })
  public noSpace: boolean = false;

  @Input({ transform: booleanAttribute })
  public blockPaste: boolean = false;

  @Output()
  public onFocus = new EventEmitter<FocusEvent>();

  @Output()
  public onBlur = new EventEmitter<FocusEvent>();

  @Output()
  public onInput = new EventEmitter<Event>();

  @Output()
  public onChange = new EventEmitter<Event>();

  @Input()
  public inputName: string = uniqueId('aprova-input-name-');

  public hasError = false;
  public errorMessageInternal: string = '';

  get isAppendSlotUsed(): boolean {
    return !!this.appendRef?.nativeElement.innerHTML;
  }

  get isErrorMessageSlotUsed(): boolean {
    return Boolean(this.errorMessageRef?.nativeElement.innerHTML);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['disabled'] && !changes['disabled'].firstChange) {
      if (this.disabled) {
        this.control.disable({ emitEvent: false });
      } else {
        this.control.enable({ emitEvent: false });
      }
      this._cdr.markForCheck();
    }
  }

  ngOnInit(): void {
    if (this.disabled) {
      this.control.disable({ emitEvent: false });
    } else {
      this.control.enable({ emitEvent: false });

      this.control.valueChanges.subscribe({
        next: () => {
          this.validate();
        },
      });

      this.control.statusChanges.subscribe({
        next: () => {
          this.validate();
        },
      });
    }
  }

  public validate(): void {
    if (this.error) {
      this.hasError = true;
      return;
    }

    const hasError = Object.values(this.control.errors || {}).some(Boolean);

    this.hasError =
      hasError &&
      this.control.invalid &&
      (this.control.dirty || this.control.touched);

    if (this.hasError) {
      const key = Object.keys(this.control.errors || {}).find(Boolean);
      this.errorMessageInternal =
        key && this.errorMessages[key] ? this.errorMessages[key] : '';
    }

    this._cdr.markForCheck();
  }

  public clear(): void {
    this.control.setValue('');
    this.focusInput();
  }

  public focusInput(): void {
    this.inputRef?.nativeElement.focus();
  }

  public handleFocus(event: FocusEvent): void {
    this.onFocus.emit(event);
  }

  public handleBlur(event: FocusEvent): void {
    const relatedTarget = event.relatedTarget as HTMLElement;
    const isClickingButton = relatedTarget?.closest('.app-create-account__btn') || 
                            relatedTarget?.closest('.app-complete-registration__btn');
    
    if (isClickingButton) {
      // Delay hiding the tooltip if we're clicking a primary action button
      setTimeout(() => {
        this.onBlur.emit(event);
        this.validate();
      }, 100);
    } else {
      this.onBlur.emit(event);
      this.validate();
    }
  }

  public handleInput(event: Event): void {
    this.onInput.emit(event);
  }

  public handleChange(event: Event): void {
    this.onChange.emit(event);
  }
}
